import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { Chip, Menu, MenuItem } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";
import { StatusBadge, ArchiveModal, ArchiveData } from "./StatusControl";
import {
  JOB_STATUS_CONFIG,
  JOB_ARCHIVE_REASONS,
  getJobDisabledReason,
} from "./StatusControl/configs";
import JobStatusTimeline from "./JobStatusTimeline";
import "./JobStatusControl.css";

interface Job {
  id: number;
  status?: string;
  status_changed_at?: string;
  archive_reason?: string;
  archive_reason_note?: string;
}

interface TransitionsResponse {
  data: {
    allowed_transitions: Array<{
      value: string;
      label: string;
    }>;
  };
}

interface ApiResponse<T> {
  data: T;
}

interface JobStatusControlProps {
  job: Job;
  disabled?: boolean;
}

export const JobStatusControl: React.FC<JobStatusControlProps> = ({
  job,
  disabled = false,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);

  const currentStatus = job?.status || "role_in_preparation";
  const statusConfig =
    JOB_STATUS_CONFIG[currentStatus] || JOB_STATUS_CONFIG.role_in_preparation;

  // Days in status
  const daysInStatus = job.status_changed_at
    ? Math.floor(
        (Date.now() - new Date(job.status_changed_at).getTime()) / 86400000,
      )
    : null;

  // Use string ID to match parent's query key (useParams returns string)
  const jobIdStr = String(job.id);

  // Fetch allowed transitions
  const { data: transitionsData } = useQuery<TransitionsResponse>(
    ["jobTransitions", jobIdStr],
    () =>
      recruitingAPI.getAllowedJobTransitions(
        job.id,
      ) as unknown as Promise<TransitionsResponse>,
    {
      enabled: !!job?.id,
      staleTime: 30 * 1000,
    },
  );

  const allowedTransitions = transitionsData?.data?.allowed_transitions || [];

  // Classify transitions into 3 zones
  const nonArchive = allowedTransitions.filter((tr) => tr.value !== "archived");
  const forwardTransition = nonArchive[0] || null;
  const fwdConfig = forwardTransition
    ? JOB_STATUS_CONFIG[forwardTransition.value]
    : null;
  const moreTransitions = nonArchive.slice(1);
  const canArchive = allowedTransitions.some((tr) => tr.value === "archived");

  // Update status mutation (optimistic)
  const updateStatusMutation = useMutation<
    ApiResponse<Job>,
    Error,
    { status: string; note?: string }
  >(
    (statusData) =>
      recruitingAPI.updateJobStatus(job.id, statusData) as unknown as Promise<
        ApiResponse<Job>
      >,
    {
      onMutate: async (statusData) => {
        await queryClient.cancelQueries(["job", jobIdStr]);
        const previousJob = queryClient.getQueryData(["job", jobIdStr]);
        queryClient.setQueryData(["job", jobIdStr], (old: any) => ({
          ...old,
          data: { ...(old?.data ?? {}), status: statusData.status },
        }));
        return { previousJob };
      },
      onError: (err, _statusData, context: any) => {
        if (context?.previousJob) {
          queryClient.setQueryData(["job", jobIdStr], context.previousJob);
        }
        console.error("Status change failed:", err);
        alert(t("recruiting:statusControl.failedToUpdate"));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["job", jobIdStr]);
        queryClient.invalidateQueries(["jobs"]);
        queryClient.invalidateQueries(["jobTransitions", jobIdStr]);
      },
    },
  );

  // Archive mutation
  const archiveMutation = useMutation<ApiResponse<Job>, Error, ArchiveData>(
    (archiveData) =>
      recruitingAPI.archiveJob(
        job.id,
        archiveData as unknown as Record<string, unknown>,
      ) as unknown as Promise<ApiResponse<Job>>,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["job", jobIdStr]);
        queryClient.invalidateQueries(["jobs"]);
        queryClient.invalidateQueries(["jobTransitions", jobIdStr]);
        setShowArchiveModal(false);
      },
      onError: (err) => {
        console.error("Archive failed:", err);
        alert(t("recruiting:statusControl.failedToArchive"));
      },
    },
  );

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === "archived") {
      setShowArchiveModal(true);
      return;
    }
    try {
      await updateStatusMutation.mutateAsync({ status: newStatus });
    } catch {
      // Error handled in mutation onError
    }
  };

  const handleArchive = async (archiveData: ArchiveData) => {
    try {
      await archiveMutation.mutateAsync(archiveData);
    } catch {
      // Error handled in mutation onError
    }
  };

  const isLoading = updateStatusMutation.isLoading || archiveMutation.isLoading;

  return (
    <div className="job-status-control" data-testid="job-status-control">
      {/* Row 1: Status badge + days in status */}
      <div className="job-status-badge-row">
        <Chip
          label={statusConfig.label}
          sx={{
            backgroundColor: statusConfig.bgColor,
            color: statusConfig.color,
            border: `2px solid ${statusConfig.color}`,
            fontWeight: 600,
            fontSize: "13px",
            height: "32px",
          }}
        />
        {daysInStatus !== null && (
          <span className="job-days-in-status">
            {t("recruiting:statusControl.daysInStatus", {
              count: daysInStatus,
            })}
          </span>
        )}
        {currentStatus === "archived" && job.archive_reason && (
          <span className="job-archive-reason">
            (
            {JOB_ARCHIVE_REASONS[job.archive_reason]?.label ||
              job.archive_reason}
            )
          </span>
        )}
      </div>

      {/* Row 2: Compact timeline */}
      <div className="job-status-timeline-row">
        <JobStatusTimeline
          currentStatus={currentStatus}
          statusChangedAt={job.status_changed_at}
        />
      </div>

      {/* Row 3: 3-zone action bar */}
      {allowedTransitions.length > 0 && !disabled && (
        <div className="job-status-actions" data-testid="job-status-actions">
          {/* Zone A: Forward CTA */}
          {forwardTransition && (
            <button
              onClick={() => handleStatusChange(forwardTransition.value)}
              disabled={isLoading}
              className="job-status-forward-btn"
              data-testid="job-status-forward-btn"
              style={{
                backgroundColor: fwdConfig?.color || "#1e40af",
                borderColor: fwdConfig?.color || "#1e40af",
              }}
            >
              {fwdConfig?.label || forwardTransition.label}
              {isLoading ? "..." : ""}
            </button>
          )}

          {/* Zone B: More dropdown */}
          {moreTransitions.length > 0 && (
            <>
              <button
                onClick={(e) => setMoreAnchorEl(e.currentTarget)}
                className="job-status-more-btn"
                data-testid="job-status-more-btn"
                aria-label={t("recruiting:statusControl.moreOptions")}
              >
                {t("recruiting:statusControl.more")} &#9662;
              </button>
              <Menu
                anchorEl={moreAnchorEl}
                open={Boolean(moreAnchorEl)}
                onClose={() => setMoreAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {moreTransitions.map((transition) => {
                  const config = JOB_STATUS_CONFIG[transition.value];
                  return (
                    <MenuItem
                      key={transition.value}
                      onClick={() => {
                        setMoreAnchorEl(null);
                        handleStatusChange(transition.value);
                      }}
                      disabled={isLoading}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: config?.color || "#6b7280",
                          display: "inline-block",
                          marginRight: 8,
                          flexShrink: 0,
                        }}
                      />
                      {config?.label || transition.label}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}

          {/* Zone C: Archive */}
          {canArchive && (
            <button
              onClick={() => setShowArchiveModal(true)}
              disabled={isLoading}
              className="job-status-archive-btn"
              data-testid="job-status-archive-btn"
            >
              &times; {t("recruiting:statusControl.archive")}
            </button>
          )}
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="job-status-loading">
          <div
            className="spinner"
            style={{ width: 20, height: 20, borderWidth: 2 }}
          />
        </div>
      )}

      {/* Archive Modal */}
      <ArchiveModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onArchive={handleArchive}
        isLoading={archiveMutation.isLoading}
        entityName={t("recruiting:statusControl.job")}
        archiveReasons={JOB_ARCHIVE_REASONS}
        customReasonInfoBox={{
          reason: "filled_externally",
          message: t("recruiting:statusControl.filledExternallyNote"),
          bgColor: "#dbeafe",
          textColor: "#1e40af",
        }}
      />
    </div>
  );
};

// Re-export STATUS_CONFIG alias for backward compatibility (used by JobsList.tsx)
export { JOB_STATUS_CONFIG as STATUS_CONFIG };

/**
 * JobStatusBadge — simple badge for use in list views
 */
export const JobStatusBadge: React.FC<{
  status?: string;
  size?: "small" | "medium" | "large";
}> = ({ status, size = "small" }) => (
  <StatusBadge
    status={status || "role_in_preparation"}
    statusConfig={JOB_STATUS_CONFIG}
    defaultStatus="role_in_preparation"
    size={size}
  />
);

/**
 * JobStatusDropdown — compact dropdown for changing job status from list views
 */
export const JobStatusDropdown: React.FC<{
  job: Job;
  onStatusChange?: (data: unknown) => void;
  disabled?: boolean;
}> = ({ job, onStatusChange, disabled = false }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["recruiting"]);
  const [isOpen, setIsOpen] = useState(false);
  const [showArchiveModalDD, setShowArchiveModalDD] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentStatus = job?.status || "role_in_preparation";
  const ddStatusConfig =
    JOB_STATUS_CONFIG[currentStatus] || JOB_STATUS_CONFIG.role_in_preparation;

  const { data: ddTransData, isLoading: loadingTransitions } =
    useQuery<TransitionsResponse>(
      ["jobTransitions", String(job?.id)],
      () =>
        recruitingAPI.getAllowedJobTransitions(
          job.id,
        ) as unknown as Promise<TransitionsResponse>,
      { enabled: !!job?.id && isOpen, staleTime: 30 * 1000 },
    );

  const ddTransitions = ddTransData?.data?.allowed_transitions || [];
  const allowedValues = new Set(ddTransitions.map((tr) => tr.value));

  const allStatuses = Object.entries(JOB_STATUS_CONFIG).map(
    ([statusKey, config]) => {
      const isAllowed = allowedValues.has(statusKey);
      const isCurrent = statusKey === currentStatus;
      let disabledReason: string | null = null;
      if (isCurrent) {
        disabledReason = "Current status";
      } else if (!isAllowed) {
        disabledReason = getJobDisabledReason(currentStatus, statusKey);
      }
      return {
        value: statusKey,
        label: config.label,
        color: config.color,
        bgColor: config.bgColor,
        isAllowed,
        isCurrent,
        disabledReason,
      };
    },
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const ddUpdateMutation = useMutation(
    (statusData: { status: string }) =>
      recruitingAPI.updateJobStatus(job.id, statusData) as unknown as Promise<
        ApiResponse<Job>
      >,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["job", String(job.id)]);
        queryClient.invalidateQueries(["jobs"]);
        queryClient.invalidateQueries(["jobTransitions", String(job.id)]);
        setIsOpen(false);
        if (onStatusChange) onStatusChange(data.data);
      },
      onError: (err: Error) => {
        console.error("Status change failed:", err);
        alert(t("recruiting:statusControl.failedToUpdate"));
      },
    },
  );

  const ddArchiveMutation = useMutation(
    (archiveData: ArchiveData) =>
      recruitingAPI.archiveJob(
        job.id,
        archiveData as unknown as Record<string, unknown>,
      ) as unknown as Promise<ApiResponse<Job>>,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["job", String(job.id)]);
        queryClient.invalidateQueries(["jobs"]);
        queryClient.invalidateQueries(["jobTransitions", String(job.id)]);
        setShowArchiveModalDD(false);
        setIsOpen(false);
        if (onStatusChange) onStatusChange(data.data);
      },
      onError: (err: Error) => {
        console.error("Archive failed:", err);
        alert(t("recruiting:statusControl.failedToArchive"));
      },
    },
  );

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) setIsOpen(!isOpen);
  };

  const handleDDStatusSelect = async (
    newStatus: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    if (newStatus === "archived") {
      setShowArchiveModalDD(true);
      return;
    }
    try {
      await ddUpdateMutation.mutateAsync({ status: newStatus });
    } catch {
      // handled in mutation
    }
  };

  const ddIsLoading = ddUpdateMutation.isLoading || ddArchiveMutation.isLoading;

  return (
    <div
      ref={dropdownRef}
      style={{ position: "relative", display: "inline-block" }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        onClick={handleBadgeClick}
        disabled={disabled}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "2px 8px",
          fontSize: "11px",
          borderRadius: "9999px",
          fontWeight: "500",
          backgroundColor: ddStatusConfig.bgColor,
          color: ddStatusConfig.color,
          border: `1px solid ${ddStatusConfig.color}`,
          cursor: disabled ? "default" : "pointer",
        }}
      >
        {ddStatusConfig.label}
        {!disabled && (
          <ChevronDown
            size={12}
            style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
          />
        )}
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            minWidth: 260,
            backgroundColor: "white",
            borderRadius: 8,
            boxShadow: "0 10px 25px -5px rgba(0,0,0,.1)",
            border: "1px solid #e5e7eb",
            zIndex: 50,
            overflow: "hidden",
          }}
        >
          {loadingTransitions ? (
            <div
              style={{ padding: "12px 16px", color: "#6b7280", fontSize: 13 }}
            >
              {t("common:loading.loading")}
            </div>
          ) : (
            <>
              <div
                style={{
                  padding: "8px 12px",
                  fontSize: 11,
                  color: "#9ca3af",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                {t("recruiting:statusControl.changeStatusTo")}
              </div>
              {allStatuses.map((status) => {
                const isDisabledItem =
                  !status.isAllowed || status.isCurrent || ddIsLoading;
                return (
                  <button
                    key={status.value}
                    onClick={(e) =>
                      !isDisabledItem && handleDDStatusSelect(status.value, e)
                    }
                    disabled={isDisabledItem}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      width: "100%",
                      padding: "10px 16px",
                      fontSize: 13,
                      textAlign: "left",
                      border: "none",
                      backgroundColor: status.isCurrent ? "#f3f4f6" : "white",
                      color: isDisabledItem ? "#9ca3af" : "#374151",
                      cursor: isDisabledItem ? "not-allowed" : "pointer",
                      opacity: isDisabledItem && !status.isCurrent ? 0.6 : 1,
                    }}
                  >
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: isDisabledItem
                            ? "#d1d5db"
                            : status.color,
                          flexShrink: 0,
                        }}
                      />
                      <span>{status.label}</span>
                      {status.isCurrent && (
                        <span
                          style={{
                            fontSize: 10,
                            padding: "1px 6px",
                            borderRadius: 4,
                            backgroundColor: status.bgColor,
                            color: status.color,
                            fontWeight: 500,
                          }}
                        >
                          {t("recruiting:statusControl.current")}
                        </span>
                      )}
                    </span>
                    {status.disabledReason && !status.isCurrent && (
                      <span
                        style={{
                          fontSize: 10,
                          color: "#9ca3af",
                          fontStyle: "italic",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {status.disabledReason}
                      </span>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}

      <ArchiveModal
        isOpen={showArchiveModalDD}
        onClose={() => setShowArchiveModalDD(false)}
        onArchive={async (data: ArchiveData) => {
          try {
            await ddArchiveMutation.mutateAsync(data);
          } catch {
            // handled
          }
        }}
        isLoading={ddArchiveMutation.isLoading}
        entityName={t("recruiting:statusControl.job")}
        archiveReasons={JOB_ARCHIVE_REASONS}
        customReasonInfoBox={{
          reason: "filled_externally",
          message: t("recruiting:statusControl.filledExternallyNote"),
          bgColor: "#dbeafe",
          textColor: "#1e40af",
        }}
      />
    </div>
  );
};

export default JobStatusControl;
